import { first } from 'lodash';
import { Text } from '@shared/components/Text';
import { View } from '@shared/components/View';
import { Pill } from '@shared/components/pill';
import { TitledWorkflowTemplate, getEntityTitle, isDeletedEntity } from '@shared/data/entity';
import { AddressField } from '@shared/data/entity/fields/AddressField';
import { DateField } from '@shared/data/entity/fields/DateField';
import { EmailField } from '@shared/data/entity/fields/EmailField';
import { Field } from '@shared/data/entity/fields/Field';
import { NumberField } from '@shared/data/entity/fields/NumberField';
import { TagField } from '@shared/data/entity/fields/TagField';
import { TextField } from '@shared/data/entity/fields/TextField';
import { BatchEditContextProvider } from '@shared/scenes/cards/entityBulkActions/BatchCreateContext';
import { WarningSubfooter } from '@shared/scenes/entity/components/WarningSubfooter';
import { AddressEditField } from '@shared/scenes/entity/components/fields/AddressEditField';
import { DateEditField } from '@shared/scenes/entity/components/fields/DateEditField/DateEditField';
import { LookupEditFieldContainerProps } from '@shared/scenes/entity/components/fields/EditFieldContainerProps';
import { EmailEditField } from '@shared/scenes/entity/components/fields/EmailEditField';
import { NumberEditField } from '@shared/scenes/entity/components/fields/NumberEditField';
import { TagEditField } from '@shared/scenes/entity/components/fields/TagEditField/TagEditField';
import { TextEditField } from '@shared/scenes/entity/components/fields/TextEditField';
import { useWorkflowTemplate } from '@shared/scenes/workflows/hooks';
import {
  getComponentLabel,
  isAddressComponent,
  isDateComponent,
  isEmailComponent,
  isNumberComponent,
  isTagComponent,
  isTextComponent,
} from '@shared/scenes/workflows/utils';
import { RELATED_LOOKUP_NAME_SEPARATOR } from '@shared/styles';
import { API } from '@shared/types/api';
import { i18n } from '@shared/util/i18n';

interface LookupEditFieldProps extends LookupEditFieldContainerProps {
  field: Field;
  entity: API.WorkflowEntity;
  sources: API.WorkflowEntity[];
  lookupComponent: API.AnyComponent;
  workflowTemplateId: string;
}

export function LookupEditField({
  field,
  entity,
  sources,
  workflowTemplateId,
  lookupComponent,
  label,
  showLabel = true,
  footer,
  error,
  style,
}: LookupEditFieldProps) {
  const isDeleted = isDeletedEntity(entity);
  const isAddressField = field instanceof AddressField;
  // sources all live in the level-1 lookup template; the leaf entity may live one
  // hop further, so each resolves its title against its own template
  const { workflowTemplate: entityWorkflowTemplate } = useWorkflowTemplate(
    entity.workflowTemplateId,
  );
  const { workflowTemplate: sourceWorkflowTemplate } = useWorkflowTemplate(
    first(sources)?.workflowTemplateId,
  );
  const headerLabel = getHeaderLabel({
    sources,
    entity,
    lookupComponent,
    sourceWorkflowTemplate,
    entityWorkflowTemplate,
  });

  const commonProps: LookupEditFieldContainerProps = {
    error,
    label,
    showLabel,
    showIcon: !isAddressField,
    header: (
      <View row paddingVertical={0.75} marginLeft={-1.25}>
        <Pill maxWidth="none">
          <Text size="overline" weight="medium" flex numberOfLines={1}>
            {headerLabel}
          </Text>
        </Pill>
      </View>
    ),
    footer,
    subfooter: (
      <WarningSubfooter
        showWarning={isDeleted}
        message={i18n('workflowTemplate.relatedCardDeleted')}
      />
    ),
    isGrayScale: isDeleted,
    isItalic: isDeleted,
    style,
  };

  if (isAddressComponent(lookupComponent) && field instanceof AddressField) {
    return <AddressEditField field={field} component={lookupComponent} {...commonProps} />;
  }
  if (isTextComponent(lookupComponent) && field instanceof TextField) {
    return <TextEditField field={field} component={lookupComponent} {...commonProps} />;
  }
  if (isNumberComponent(lookupComponent) && field instanceof NumberField) {
    return <NumberEditField field={field} component={lookupComponent} {...commonProps} />;
  }
  if (isEmailComponent(lookupComponent) && field instanceof EmailField) {
    return <EmailEditField field={field} component={lookupComponent} {...commonProps} />;
  }
  if (isDateComponent(lookupComponent) && field instanceof DateField) {
    return (
      <BatchEditContextProvider>
        <DateEditField
          field={field}
          component={lookupComponent}
          workflowTemplateId={workflowTemplateId}
          {...commonProps}
        />
      </BatchEditContextProvider>
    );
  }
  if (isTagComponent(lookupComponent) && field instanceof TagField) {
    return (
      <TagEditField
        field={field}
        component={lookupComponent}
        workflowTemplateId={workflowTemplateId}
        {...commonProps}
      />
    );
  }

  return null;
}

function getHeaderLabel({
  sources,
  entity,
  lookupComponent,
  sourceWorkflowTemplate,
  entityWorkflowTemplate,
}: {
  sources: API.WorkflowEntity[];
  entity: API.WorkflowEntity;
  lookupComponent: API.AnyComponent;
  sourceWorkflowTemplate?: TitledWorkflowTemplate;
  entityWorkflowTemplate?: TitledWorkflowTemplate;
}): string {
  const untitled = i18n('workflowTemplate.relatedCardLookupUntitled');
  const sourceNames = sources.map(
    source => getEntityTitle(source, sourceWorkflowTemplate) ?? untitled,
  );
  const entityName = getEntityTitle(entity, entityWorkflowTemplate) ?? untitled;
  const componentLabel = getComponentLabel(lookupComponent);
  return [...sourceNames, entityName, componentLabel].join(` ${RELATED_LOOKUP_NAME_SEPARATOR} `);
}
